import type { PricingStrategyValues } from 'components/PricingStrategySection';
import { formatTimeForApi } from 'components/TimeInput/TimeInput';
import dayjs from 'dayjs';
import { isLineItemReadyForCosting } from 'screens/Authenticated/NewBooking/venueDetailsCostingUtils';
import { isCustomizePackage, normalizeCustomPricePackageName } from 'utils/pricePackage';

const unwrapDropdownValue = (value: unknown): unknown => {
  if (value != null && typeof value === 'object' && 'value' in (value as object)) {
    return (value as { value?: unknown }).value;
  }
  return value;
};

type BuildAddLineItemCostingRequestArgs = {
  formData: Record<string, any>;
  eventType: any;
  customerName?: string;
  pricing: PricingStrategyValues;
  bookedAddOns: any[];
  hourlyPricing: boolean;
  pricePackages: { value: any; label: string }[];
};

/** Builds POST `/new-booking/costing` body from the add-line-item draft. */
export const buildAddLineItemCostingRequest = ({
  formData,
  eventType,
  customerName,
  pricing,
  bookedAddOns,
  hourlyPricing,
  pricePackages,
}: BuildAddLineItemCostingRequestArgs): Record<string, any> | null => {
  if (!isLineItemReadyForCosting(formData, hourlyPricing)) {
    return null;
  }

  const startDate = dayjs(formData.startDate);
  const endDate = dayjs(formData.endDate);
  if (!startDate.isValid() || !endDate.isValid()) return null;

  const formattedStart = startDate.format('DD-MM-YYYY');
  const formattedEnd = endDate.format('DD-MM-YYYY');
  const venueId = unwrapDropdownValue(formData.venue);
  const eventTypeId = unwrapDropdownValue(eventType) ?? unwrapDropdownValue(formData.eventType);
  const partOfDay = unwrapDropdownValue(formData.partOfDay);
  const pricePackageId = unwrapDropdownValue(formData.pricePackage);
  const layoutId = unwrapDropdownValue(formData.layout);
  const status = unwrapDropdownValue(formData.status);

  const name = String(customerName ?? '').trim();
  const [firstName, ...restName] = name.split(/\s+/).filter(Boolean);

  const serviceChargePercentage =
    formData.serviceCharge != null && typeof formData.serviceCharge === 'object'
      ? (formData.serviceCharge as { value?: unknown }).value
      : formData.serviceChargePercentage ?? formData.serviceCharge;

  return {
    firstName: firstName || 'Guest',
    lastName: restName.join(' ') || undefined,
    startDate: formattedStart,
    endDate: formattedEnd,
    eventType: eventTypeId,
    complementary: false,
    holdDays: 0,
    // Required by BookingRequestValidator (percentages must total 100).
    minConfirmPaymentPercentage: 100,
    paymentSlotOnePercentage: 0,
    paymentSlotTwoPercentage: 0,
    paymentSlotThreePercentage: 0,
    paymentSlotOneBeforeDays: 0,
    paymentSlotTwoBeforeDays: 0,
    fullPaymentBeforeDays: 0,
    bookedRooms: [],
    externalBookedRooms: [],
    venues: [
      {
        venue: venueId,
        eventSubtype:
          typeof formData.eventSubtype === 'object'
            ? (formData.eventSubtype as any)?.label || (formData.eventSubtype as any)?.value || ''
            : formData.eventSubtype,
        placeOfEvent: formData.placeOfEvent,
        addressOfEvent: formData.addressOfEvent,
        guests: Number(formData.guests),
        excessGuestsPercentage:
          formData.excessGuestsPercentage === '' || formData.excessGuestsPercentage == null
            ? null
            : Number(formData.excessGuestsPercentage),
        startDate: formattedStart,
        endDate: formattedEnd,
        startTime: formatTimeForApi(formData.startTime as string),
        endTime: formatTimeForApi(formData.endTime as string),
        partOfDay,
        status,
        pricePackageId: hourlyPricing ? null : pricePackageId,
        customPricePackageName:
          !hourlyPricing && isCustomizePackage(pricePackageId, pricePackages)
            ? normalizeCustomPricePackageName(formData.customPricePackageName)
            : null,
        layout: layoutId || '',
        preLeadTime: formData.preLeadTime,
        postLeadTime: formData.postLeadTime,
        taxGroupOnGuestChargeId:
          unwrapDropdownValue(formData.taxGroupOnGuestCharge) ?? formData.taxGroupOnGuestCharge ?? null,
        taxGroupOnHallRentalId:
          unwrapDropdownValue(formData.taxGroupOnHallRental) ?? formData.taxGroupOnHallRental ?? null,
        taxGroupOnServiceChargeId:
          unwrapDropdownValue(formData.taxGroupOnServiceCharge) ?? formData.taxGroupOnServiceCharge ?? null,
        serviceChargePercentage,
        pricePerPerson: !hourlyPricing && pricing.overridePricePerPerson ? pricing.pricePerPerson : null,
        minimumRevenue: !hourlyPricing && pricing.overrideMinimumRevenue ? pricing.minimumRevenue : null,
        hallRental: !hourlyPricing && pricing.overrideHallRental ? pricing.hallRental : null,
        pricePerHour: hourlyPricing && pricing.overridePricePerHour ? pricing.pricePerHour : null,
        overridePricePerPerson: pricing.overridePricePerPerson,
        overrideMinimumRevenue: pricing.overrideMinimumRevenue,
        overrideHallRental: pricing.overrideHallRental,
        overridePricePerHour: pricing.overridePricePerHour,
        addOns: bookedAddOns,
      },
    ],
  };
};
